// Odkaz pro lidi mimo firmu (jádro PublicLinks): dialog s volbami podle pravidel správce – u složky co přes odkaz jde
// (prohlížet a stáhnout / i nahrát / jen nahrát), platnost (jen do stropu správce), heslo (povinné, když to správce chce);
// po vytvoření adresa s Kopírovat. Používají ho Soubory (Odkaz pro lidi mimo firmu…) a Pošta (chytrá příloha).
import { core, ApiError } from '../api';
import { h } from '../dom';
import { t, tk, fmtDate } from '../i18n';
import { icon } from '../icons';
import { dialog } from './dialog';
import { toast, toastError } from './toast';

export interface LinkPolicy { enabled: boolean; maxDays: number; passwordRequired: boolean; uploadAllowed: boolean; allowed: boolean }
export interface CreatedLink { id: string; path: string; name: string; size: number; kind: string; perm: string; expiresAt: string | null; password: boolean }

let policyCache: Promise<{ policy: LinkPolicy; days: number[] }> | null = null;
export function linkPolicy(): Promise<{ policy: LinkPolicy; days: number[] }> {
  policyCache ??= core<{ policy: LinkPolicy; days: number[] }>('links', 'policy').catch((e) => { policyCache = null; throw e; });
  return policyCache;
}

/** Platnosti, které pravidla dovolují (0 = bez omezení jen bez stropu). */
export function allowedDays(policy: LinkPolicy, days: number[]): number[] {
  return days.filter((d) => policy.maxDays === 0 || (d > 0 && d <= policy.maxDays));
}
const dayLabel = (d: number): string => (d ? t('core.links.days', { n: d }) : t('core.links.forever'));

export interface LinkOptions { perm: string; days: number; password: string }

/** Dialog s volbami odkazu; null = zrušeno nebo odkazy nejsou povolené (hláška). */
export async function linkOptionsDialog(names: string[], folder: boolean): Promise<LinkOptions | null> {
  let pol: { policy: LinkPolicy; days: number[] };
  try {
    pol = await linkPolicy();
  } catch (e) {
    toastError(e);
    return null;
  }
  if (!pol.policy.allowed) {
    toast(t('core.links.disabled'), 'error');
    return null;
  }
  const days = allowedDays(pol.policy, pol.days);
  const def = days.includes(30) ? 30 : (days[days.length - 1] ?? 7);
  const daySel = h('select', { class: 'input' }, days.map((d) => h('option', { value: String(d), selected: d === def }, dayLabel(d)))) as HTMLSelectElement;
  const perms = folder ? ['read', ...(pol.policy.uploadAllowed ? ['readwrite', 'upload'] : [])] : ['read'];
  const permSel = h('select', { class: 'input' }, perms.map((p) => h('option', { value: p }, t('core.links.perm_' + p)))) as HTMLSelectElement;
  const pw = h('input', { class: 'input', type: 'text', autocomplete: 'off', placeholder: pol.policy.passwordRequired ? t('core.links.password_required_ph') : t('core.links.password_optional_ph') }) as HTMLInputElement;
  const field = (label: string, el: HTMLElement) => h('label', { class: 'field' }, h('span', { class: 'field-label' }, label), el);
  const r = await dialog({
    title: t('core.links.dialog_title'),
    icon: 'link',
    message: h('div', { class: 'link-dlg' },
      h('p', { class: 'muted' }, t(folder ? 'core.links.intro_folder' : 'core.links.intro_file', { name: names.join(', ') })),
      folder ? field(t('core.links.what'), permSel) : null,
      field(t('core.links.validity'), daySel),
      field(t('core.links.password_field'), pw)),
    buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t('core.links.create'), primary: true }],
  });
  if (r.id !== 'ok') return null;
  if (pol.policy.passwordRequired && !pw.value.trim()) {
    toast(t('core.links.password_required'), 'error');
    return null;
  }
  if (pw.value.trim() && pw.value.trim().length < 8) {
    toast(t('core.links.password_short'), 'error');
    return null;
  }
  return { perm: folder ? permSel.value : 'read', days: Number(daySel.value), password: pw.value.trim() };
}

export async function createLink(itemId: string, o: LinkOptions, context: Record<string, unknown> = {}): Promise<CreatedLink | null> {
  try {
    return (await core<{ link: CreatedLink }>('links', 'create', { id: itemId, days: o.days, password: o.password || null, perm: o.perm, context })).link;
  } catch (e) {
    toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    return null;
  }
}

export const linkUrl = (path: string): string => {
  const prefix = (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';
  return location.origin + prefix + path;
};

/** Hotový odkaz: adresa ke zkopírování, platnost, heslo (pošlete jinou cestou). */
export async function showLink(link: CreatedLink, password: string): Promise<void> {
  const url = linkUrl(link.path);
  const input = h('input', { class: 'input link-url', type: 'text', value: url, readonly: true }) as HTMLInputElement;
  const copy = h('button', { type: 'button', class: 'btn primary' }, icon('copy'), t('core.links.copy'));
  copy.addEventListener('click', () => void navigator.clipboard?.writeText(url).then(() => toast(t('core.links.copied'), 'ok')));
  input.addEventListener('focus', () => input.select());
  await dialog({
    title: t('core.links.ready'),
    icon: 'link',
    message: h('div', { class: 'link-dlg' },
      h('p', null, t(link.kind === 'folder' ? 'core.links.perm_' + link.perm : 'core.links.perm_read_file'), ' · ', link.expiresAt ? t('core.links.until', { date: fmtDate(new Date(link.expiresAt.replace(' ', 'T') + 'Z')) }) : t('core.links.forever')),
      h('div', { class: 'link-url-row' }, input, copy),
      password ? h('p', { class: 'muted small' }, t('core.links.password_note')) : null,
      h('p', { class: 'muted small' }, t('core.links.where'))),
    buttons: [{ id: 'ok', label: t('core.ui.close'), primary: true }],
  });
}

/** Celý postup pro jednu položku (Soubory): volby → vytvořit → ukázat adresu. */
export async function publicLinkFlow(item: { id: string; name: string; kind: string }): Promise<boolean> {
  const o = await linkOptionsDialog([item.name], item.kind === 'folder');
  if (!o) return false;
  const link = await createLink(item.id, o, { app: 'soubory' });
  if (!link) return false;
  await showLink(link, o.password);
  return true;
}
